import { useState, useEffect, useCallback } from "react"
import { useHistory, useLocation } from "react-router-dom"

import LoadingButton from "@mui/lab/LoadingButton"

import {
    Box,
    Button,
    Card,
    CardHeader,
    IconButton,
    CardContent,
    List,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    Checkbox,
    ListItemText,
    Switch,
    FormControlLabel,
    FormGroup,
    ToggleButtonGroup,
    ToggleButton
} from "@mui/material"

import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as ContentCopyIcon,
    Groups as GroupsIcon,
    Add as AddIcon,
    ViewKanban as ViewKanbanIcon,
    ListAlt as ListAltIcon,
    TableRows as TableRowsIcon,
    Download as DownloadIcon,
    Visibility as EyeIcon
} from "@mui/icons-material"

import useNotification from "../Notification"
import Loading from "../Loading"
import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"
import { getSession } from "../../auth"
import api from "../../api"
import { formatCurrencyValue, formatCurrency } from "./functions"

import Kanban from "./kanban"

import JourneyDashboard from "./dashboard"
import UserPermissions from "../User/Permissions"

import {
    DataGridPro,
    ptBR
} from '@mui/x-data-grid-pro'

import dayjs from 'dayjs'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
}

export const colorOptionsVivid = [
    { name: "Vermelho", value: "#f44336" },
    { name: "Rosa", value: "#e91e63" },
    { name: "Azul", value: "#2196f3" },
    { name: "Ciano", value: "#00bcd4" },
    { name: "Verde", value: "#4caf50" },
    { name: "Lima", value: "#cddc39" },
    { name: "Amarelo", value: "#ffeb3b" },
    { name: "Âmbar", value: "#ffc107" },
    { name: "Laranja", value: "#ff9800" },
    { name: "Cinza", value: "#9e9e9e" }
]

export const colorOptionsMedium = [
    { name: "Vermelho", value: "#f78a81" },
    { name: "Rosa", value: "#ed7d99" },
    { name: "Azul", value: "#6ca8e3" },
    { name: "Ciano", value: "#63d4e4" },
    { name: "Verde", value: "#78c889" },
    { name: "Lima", value: "#d5e562" },
    { name: "Amarelo", value: "#fff176" },
    { name: "Âmbar", value: "#ffca55" },
    { name: "Laranja", value: "#ffae5b" },
    { name: "Cinza", value: "#b0b0b0" }
]

export const colorOptionsLight = [
    { name: "Vermelho", value: "#f8bdbd" },
    { name: "Rosa", value: "#f4b3c2" },
    { name: "Azul", value: "#a8c9f0" },
    { name: "Ciano", value: "#a4e4ec" },
    { name: "Verde", value: "#b8dfba" },
    { name: "Lima", value: "#e4efaa" },
    { name: "Amarelo", value: "#fff9b1" },
    { name: "Âmbar", value: "#ffe199" },
    { name: "Laranja", value: "#ffc89a" },
    { name: "Cinza", value: "#cfcfcf" }
]

const exportToCsv = (rows, columns) => {

    const columnWithoutAction = columns.flatMap(column => {
        if (column.field === 'actions') return []
        return column
    })

    const headers = columnWithoutAction.map(column => column.headerName)

    const csvRows = rows.map(row => {
        const baseFields = columnWithoutAction.map(column => {
            let value = row[column.field]
            if ('valueGetter' in column)
                value = column.valueGetter({value, row}) 
            if ('valueFormatter' in column)
                value = column.valueFormatter({value, row}) 

            return value
        })

        return [...baseFields].map(field => `"${String(field).replace(/"/g, '""')}"`)
    })

    const csvContent = [headers.join(','), ...csvRows.map(row => row.join(','))].join('\n')
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${'Jornadas'}_${dayjs().format('YYYY-MM-DD')}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

const listColumns = ({setView=()=>{},setEdit=()=>{}, setDelete=()=>{}, setCopy=()=>{}, groups={}}) => ([
    { field: "name", headerName: "Jornada", flex: 1 },
    { field: 'columns', headerName: 'Qtd. Colunas', valueGetter: ({value}) => (value || []).length, flex: 1},
    { field: 'users', headerName: 'Qtd. Usuários', valueGetter: ({value}) => (value || []).length, flex: 1}, 
    { field: 'total_opp', headerName: 'Qtd. Oportunidades', valueGetter: ({row}) => row.columns.reduce((a, b) => b.opportunity_count + a, 0), flex: 1},
    { field: 'total_value', headerName: 'Valor total', valueGetter: ({row}) => row.columns.reduce((a, b) => b.total_value + a, 0), valueFormatter: ({value}) => `R$ ${formatCurrencyValue(value)}`, flex: 1},  
    { field: "actions", headerName: "Ações", width: groups.isSupervisor ? 140: 60, renderCell: ({row}) => (
            <Box>
                <IconButton sx={{ padding: ".25rem" }}><EyeIcon sx={{ fontSize: "1.25rem" }} onClick={()=>setView(row)} /></IconButton>
                {groups.isSupervisor && 
                    <>
                        <IconButton sx={{ padding: ".25rem" }}><EditIcon sx={{ fontSize: "1.25rem" }} onClick={()=>setEdit(row)} /></IconButton>
                        <IconButton sx={{ padding: ".25rem" }}><ContentCopyIcon sx={{ fontSize: "1.25rem" }} onClick={()=>setCopy(row)} /></IconButton>
                        <IconButton sx={{ padding: ".25rem" }}><DeleteIcon sx={{ fontSize: "1.25rem", color: "#c95f50" }} onClick={() => setDelete(row)} /></IconButton>
                    </>
                }
            </Box>
    ) }
])

const initialJourneyState = { name: "", description: "", color: "#6ca8e3", users: [], required_opportunity_fields: [] }

const MultiSelectInput = ({ options, selected, setSelected, fixedIds = [] }) => {
    const handleChange = (event) => {
        const {
            target: { value },
        } = event
        const newValue = typeof value === 'string' ? value.split(',') : value
        const merged = Array.from(new Set([...fixedIds, ...newValue]))
        setSelected(merged)
    }

    return (
        <FormControl fullWidth size="small">
            <InputLabel>Membros</InputLabel>
            <Select
                multiple
                value={selected}
                onChange={handleChange}
                input={<OutlinedInput label="Membros" />}
                renderValue={(selectedIds) =>
                    selectedIds.map((id) => {
                        const user = options.find((u) => u.id === id)
                        return user ? `${user.first_name} ${user.last_name}` : id
                    }).join(', ')
                }
                MenuProps={MenuProps}
            >
                {options.map((item) => (
                    <MenuItem key={`${item.first_name} ${item.last_name}`} value={item.id} disabled={fixedIds.includes(item.id)}>
                        <ListItemText primary={`${item.first_name} ${item.last_name}`} />
                        {fixedIds.includes(item.id) && <span style={{color: '#888', fontSize: 12, marginLeft: 8}}>(Supervisor)</span>}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

const Journeys = () => {

    const [journeys, setJourneys] = useState([])
    const [users, setUsers] = useState([])
    const [showCreateUpdateJourney, setShowCreateUpdateJourney] = useState(false)
    const [selectedJourney, setSelectedJourney] = useState()
    const [newJourney, setNewJourney] = useState(initialJourneyState)
    const [duplicateJourney, setDuplicateJourney] = useState(null)
    const [showDuplicateModal, setShowDuplicateModal] = useState(false)
    const [duplicateColumns, setDuplicateColumns] = useState(true)
    const [duplicateAutomations, setDuplicateAutomations] = useState(true)
    const [duplicateRequiredFields, setDuplicateRequiredFields] = useState(false)
    const [showEditRequiredFields, setShowEditRequiredFields] = useState(false)
    const [editingRequiredJourney, setEditingRequiredJourney] = useState(null)
    const [previousAutomationsState, setPreviousAutomationsState] = useState(true)
    const [toDelete, setToDelete] = useState()
    const [loading, setLoading] = useState(false)
    const [dashboard, setDashboard] = useState(null)
    const [viewMode, setViewMode] = useState(localStorage.getItem('journey-board-view') || 'board')
    const [listFilterModel, setListFilterModel] = useState({ items: [] })

    //Ordering
    const [dragItem, setDragItem] = useState(null)
    const [placeholderIndex, setPlaceholderIndex] = useState(null)

    const history = useHistory()
    const location = useLocation()

    const groups = UserPermissions()
    const session = getSession && getSession()
    const loggedUserName = session?.username || session?.profile?.username || session?.profile?.user?.username
    const loggedUserObj = users.find(u => u.username === loggedUserName)
    const loggedUserId = loggedUserObj?.id

    const createJourney = async (data) => {
        const enterpriseId = getSession().profile.enterprise.id
        setLoading(true)
        const response = await api.post(`api/enterprises/${enterpriseId}/journeys/`, data)
        if (response.status === 201) {
            const safeJourney = {
                users: response.data.users || [],
                columns: response.data.columns || [],
                ...response.data
            }
            setJourneys((prevState) => [...prevState, safeJourney])
            setShowCreateUpdateJourney(false)
            setNewJourney(initialJourneyState)
            setLoading(false)
            useNotification("Sucesso", "Jornada criada com sucesso.", "success")
            return
        }
        setLoading(false)
        useNotification("Ops!", "Algo deu errado, tente novamente.", "danger")
    }

    const updateJourney = async (data) => {
        const enterpriseId = getSession().profile.enterprise.id
        setLoading(true)
        const response = await api.patch(`api/enterprises/${enterpriseId}/journeys/${data.id}/`, data)
        if (response.status === 200) {
            const safeData = {
                users: data.users || [],
                columns: data.columns || [],
                ...data
            }
            setJourneys((prevJourneys) => prevJourneys.map((journey) => journey.id === data.id ? { ...journey, ...safeData } : journey))
            setShowCreateUpdateJourney(false)
            setNewJourney(initialJourneyState)
            setLoading(false)
            useNotification("Sucesso", "Jornada atualizada com sucesso.", "success")
            return
        } else if (response.status === 400){
            setLoading(false)
            useNotification("Ops!", "Jornada não encontrada!", "danger")
            return
        }
        setLoading(false)
        useNotification("Ops!", "Algo deu errado, tente novamente.", "danger")
    }

    const deleteJourney = async (data) => {
        const enterpriseId = getSession().profile.enterprise.id
        setLoading(true)
        const response = await api.delete(`api/enterprises/${enterpriseId}/journeys/${data.id}/`)
        if (response.status === 204) {
            setToDelete(null)
            setJourneys((prevJourneys) => prevJourneys.filter((b) => b.id !== data.id))
            setLoading(false)
            useNotification("Sucesso", "Jornada removida com sucesso.", "success")
            return
        }
        else if (response.status === 400){
            setLoading(false)
            useNotification("Ops!", "Jornada não encontrada!", "danger")
            return
        }
        setLoading(false)
        useNotification("Ops!", "Algo deu errado, tente novamente.", "danger")
    }

    const duplicateJourneyAction = async (originalJourneyId, data) => {
        const enterpriseId = getSession().profile.enterprise.id
        setLoading(true)
        const response = await api.post(`api/enterprises/${enterpriseId}/journeys/${originalJourneyId}/duplicate/`, data)
        if (response.status === 200) {
            const safeJourney = {
                users: response.data.users || [],
                columns: response.data.columns || [],
                ...response.data
            }
            setJourneys((prevState) => [...prevState, safeJourney])
            setShowDuplicateModal(false)
            setDuplicateJourney(null)
            setLoading(false)
            useNotification("Sucesso", "Jornada duplicada com sucesso.", "success")
            return
        }
        setLoading(false)
        useNotification("Ops!", "Algo deu errado, tente novamente.", "danger")
    }

    const reorderJourneys = (list) => {
        const order = JSON.parse(localStorage.getItem('journey-ordering') || '[]')
        const orderMap = new Map(order.map((id, index) => [id, index]));
        return list.toSorted((a, b) => orderMap.get(a.id) - orderMap.get(b.id))
    }

    const refetchJourneys = async () => {
        setLoading(true)
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.get(`api/enterprises/${enterpriseId}/journeys/`)
        if (response.status === 200)
            setJourneys(reorderJourneys(response.data || []))
        setLoading(false)
    }

    useEffect(() => {
        const enterpriseId = getSession().profile.enterprise.id
        const fetchData = async () => {
            setLoading(true)
            const [journeyResponse, usersResponse] = await Promise.all([
                api.get(`api/enterprises/${enterpriseId}/journeys/`),
                api.get(`api/enterprises/${enterpriseId}/users/get_users_emails/`)
            ])
            if (journeyResponse.status === 200){
                const safeData = (journeyResponse.data || []).map(j => ({
                    users: j.users || [],
                    columns: j.columns || [],
                    ...j
                }))
                setJourneys(reorderJourneys(safeData))
            }
                
            if (usersResponse.status === 200) setUsers(usersResponse.data)

            const urlParams = new URLSearchParams(window.location.search)
            
            const journeysList = journeyResponse.data?.results || journeyResponse.data || []
            const urlJourneyId = urlParams.get('selectedJourney') || urlParams.get('journey')
            const journey = Array.isArray(journeysList) ? journeysList.find(journey => journey.id === Number(urlJourneyId)) : null

            if (journey)
                setSelectedJourney(journey)

            setLoading(false)
        }
        fetchData()
    }, [])

    useEffect(() => {
        if (showCreateUpdateJourney && groups.isSupervisor && !groups.isAdmin && loggedUserId) {
            const alreadyIncluded = Array.isArray(newJourney.users) && newJourney.users.includes(loggedUserId)
            if (!alreadyIncluded) {
                setNewJourney(prev => ({ ...prev, users: [...(prev.users || []), loggedUserId] }))
            }
        }
    }, [showCreateUpdateJourney, groups.isSupervisor, groups.isAdmin, loggedUserId])


    const handleDragEnd = (...args) => {
        setDragItem(null);
        setPlaceholderIndex(null);
    }

    const handleDragStart = (e, item, index = null) => {
        e.dataTransfer.setData("text/plain", item);
        setDragItem(item);
        setPlaceholderIndex(null);
    }

    const handleDragOverForm = (e) => {
        e.preventDefault();

        const mouseY = e.clientY;
        const mouseX = e.clientX

        if (journeys.length === 0) {
            setPlaceholderIndex(0);
            return;
        }

        let newIndex = journeys.length;
        let lesserDelta = 50000

        for (let i = 0; i < journeys.length; i++) {
            const element = journeys[i]
            const el = document.getElementById(`card-item-${element.id}`);
            if (!el) continue;

            const rect = el.getBoundingClientRect();
            const middleY = ((rect.y+rect.height/2) - mouseY)**2
            const middleX = ((rect.x+rect.width/2) - mouseX)**2
            const delta = (middleY+middleX)**1/2
            
            if (delta <= lesserDelta){
                newIndex = i;
                lesserDelta = delta
            }
        }

        setPlaceholderIndex(newIndex);
    }

    const handleDropForm = (e) => {
        e.preventDefault();

        const newOrdering = insertAtPosition(journeys, dragItem, placeholderIndex);

        setJourneys(newOrdering);

        //Save localstorage
        localStorage.setItem('journey-ordering', JSON.stringify(newOrdering.map(i => i.id)))

        setPlaceholderIndex(null);
    };

    function insertAtPosition(form, draggedKey, newPosition) {

        // posição antiga
        const oldPosition = form.findIndex(i => i.id === draggedKey)

        // se a posição não mudou, não faz nada
        if (oldPosition === newPosition) return form;

        // cria uma cópia
        const updated = structuredClone(form);

        const item = updated.splice(oldPosition, 1)[0]; 
        updated.splice(newPosition, 0, item); 

        return updated;
    }

    const baseCopy = (item) => {
        const suffix = ' - Cópia';
        const maxLength = 50;
        const baseName = item.name.length + suffix.length > maxLength 
            ? item.name.substring(0, maxLength - suffix.length)
            : item.name;
        
        setDuplicateJourney({
            ...item,
            name: baseName + suffix
        });
        setDuplicateColumns(true);
        setDuplicateAutomations(true);
        setShowDuplicateModal(true);
    }

    const supervisorFixedIds = groups.isSupervisor && !groups.isAdmin && loggedUserId ? [loggedUserId] : []
    const handleSelectedUsers = useCallback((newVal) => { setNewJourney(prev => ({ ...prev, users: newVal })) }, [])

    return (
        <>
            <Loading loading={loading} />
            {
                dashboard ?
                    <JourneyDashboard allUsers={users} journeys={journeys} handleClose={() => setDashboard(false)} />
                    :
                    selectedJourney
                        ? <Kanban
                            selectedJourney={selectedJourney}
                            close={() => {
                                setSelectedJourney(null)
                                const params = new URLSearchParams(location.search)
                                params.delete("selectedJourney")
                                params.delete("latestChanged")
                                params.delete("journey")
                                history.push({
                                    pathname: location.pathname,
                                    search: params.toString(),
                                })
                                refetchJourneys()
                            }}
                            allUsers={users}
                        />
                        : (
                            <Box className="journeys-container">

                                <Box className="journeys-header">
                                    {groups.isSupervisor &&
                                        <LoadingButton
                                            onClick={() => {
                                                const supervisorId = (groups.isSupervisor && !groups.isAdmin) ? users.find(u => u.username === loggedUserName)?.id : null
                                                setNewJourney({ ...initialJourneyState, users: supervisorId ? [supervisorId] : [] })
                                                setShowCreateUpdateJourney(true)
                                            }}
                                            variant={"contained"}
                                            startIcon={<AddIcon />}
                                        >
                                            Nova Jornada
                                        </LoadingButton>
                                    }
                                    <Button
                                        onClick={() => {
                                            setDashboard(true)
                                        }}
                                    >
                                        Relatórios
                                    </Button>
                                    <ToggleButtonGroup
                                        orientation="horizontal"
                                        value={viewMode}
                                        exclusive
                                        onChange={(event, nextView) => {
                                            if (nextView){
                                                localStorage.setItem('journey-board-view', nextView)
                                                setViewMode(nextView)
                                            }
                                                
                                        }}
                                        size="small"
                                        className="toggle-view-mode-btn"
                                    >
                                        <ToggleButton value="board">
                                            <ViewKanbanIcon />
                                        </ToggleButton>
                                        <ToggleButton value="list">
                                            <TableRowsIcon />
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                    {viewMode === "list" &&
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => exportToCsv(journeys, listColumns({}))}
                                        >
                                            Exportar CSV
                                        </Button>
                                    }
                                </Box>
                                <Box className="journeys-scroll">
                                {viewMode === 'board' && 
                                    <>
                                        {!loading && journeys.length === 0 &&
                                            <Box className="journeys-info">
                                                <ViewKanbanIcon />
                                                <Typography variant="h6">
                                                    Você ainda não possui nenhuma jornada.
                                                </Typography>
                                                <Typography variant="h6">
                                                    Clique no botão acima para criar.
                                                </Typography>
                                            </Box>
                                        }
                                        <Box className="journeys-content" 
                                            onDrop={handleDropForm}
                                            onDragOver={handleDragOverForm}
                                        >
                                            {journeys.map((item, index) => (
                                                <>
                                                <Card 
                                                draggable 
                                                onDragStart={(e) => handleDragStart(e, item.id, index)}
                                                onDragEnd={handleDragEnd}
                                                key={`card-item-${item.id}`} 
                                                id={`card-item-${item.id}`}
                                                onClick={() => {
                                                    setSelectedJourney(item)
                                                    const params = new URLSearchParams(location.search)
                                                    params.set("selectedJourney", item.id)
                                                    history.push({
                                                        pathname: location.pathname,
                                                        search: params.toString(),
                                                    })
                                                }} 
                                                sx={{ flex: "1 1 calc(25% - 1rem)", maxWidth: "25%", cursor: "pointer", backgroundColor: item.color,
                                                    cursor:'grab', userSelect: 'none',
                                                    ...(index === placeholderIndex ? {boxShadow: '1px 1px 16px 1px #008aff', opacity: '.5'}:{})
                                                 }}>
                                                    <CardHeader
                                                        title={item.name}
                                                        //subheader={item.description}

                                                        action={
                                                            <>
                                                                {
                                                                    groups.isSupervisor &&
                                                                    <Box>
                                                                        <IconButton onClick={(e) => { e.stopPropagation(); setShowCreateUpdateJourney(true); setNewJourney({ ...item }) }}>
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                        <IconButton onClick={(e) => { 
                                                                            e.stopPropagation(); 
                                                                            const suffix = ' - Cópia';
                                                                            const maxLength = 50;
                                                                            const baseName = item.name.length + suffix.length > maxLength 
                                                                                ? item.name.substring(0, maxLength - suffix.length)
                                                                                : item.name;
                                                                            
                                                                            setDuplicateJourney({
                                                                                ...item,
                                                                                name: baseName + suffix
                                                                            });
                                                                            setDuplicateColumns(true);
                                                                            setDuplicateAutomations(true);
                                                                            setDuplicateRequiredFields(false);
                                                                            setShowDuplicateModal(true);
                                                                        }}>
                                                                            <ContentCopyIcon />
                                                                        </IconButton>
                                                                        <IconButton onClick={(e) => { e.stopPropagation(); setToDelete(item) }}>
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Box>

                                                                }
                                                            </>
                                                        }
                                                        titleTypographyProps={{
                                                            fontSize: 18
                                                        }}
                                                        subheaderTypographyProps={{
                                                            fontSize: 14,
                                                            //width: "17rem",
                                                            height: "2rem",
                                                            padding: ".25rem"
                                                        }}
                                                    />
                                                    <CardContent className="journey-card-content">
                                                        <Box className='journey-card-header-description'>
                                                            {item.description}
                                                        </Box>
                                                        <Box className="members-row">
                                                            <GroupsIcon />
                                                            {(item.users || []).length}
                                                        </Box>
                                                        <List dense sx={{
                                                            overflow: 'auto',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            maxHeight: '10rem',
                                                            paddingRight: '.5rem'
                                                        }}>
                                                            {(item.columns || []).length === 0 &&
                                                                <Box className="journey-card-info">
                                                                    Esta jornada ainda não possui nenhuma coluna, clique para editar
                                                                </Box>
                                                            }
                                                            {(item.columns || []).map((col, i) => (
                                                                <Box key={i} display="grid" gridTemplateColumns="1fr auto" width="100%" alignItems="center">
                                                                    <Typography>{col.title}</Typography>
                                                                    <Typography>
                                                                        Qtd: {col.opportunity_count} - Valor: {formatCurrencyValue(col.total_value)}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </List>
                                                    </CardContent>
                                                </Card>
                                                </>
                                            ))}
                                        </Box>
                                    </>
                                }
                                {viewMode === 'list' && 
                                    <Box className="table-container" height='100%'>
                                        <DataGridPro
                                            getRowClassName={({row}) => `table-container-background-color-${row.color.replace('#', '')}`}
                                            rows={journeys}
                                            columns={listColumns({
                                                setView  : (item) => {
                                                    setSelectedJourney(item)
                                                    const params = new URLSearchParams(location.search)
                                                    params.set("selectedJourney", item.id)
                                                    params.delete("journey")
                                                    history.push({
                                                        pathname: location.pathname,
                                                        search: params.toString(),
                                                    })
                                                },
                                                setEdit  : (item) => {setShowCreateUpdateJourney(true); setNewJourney({ ...item })},
                                                setCopy  : (item) => baseCopy(item),
                                                setDelete: (item) => setToDelete(item),
                                                groups   : groups
                                            })}
                                            filterModel={listFilterModel}
                                            onFilterModelChange={(newFilterModel) => setListFilterModel(newFilterModel)}
                                            disableRowSelectionOnClick
                                            density="compact"
                                            hideFooter
                                            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                                        />
                                    </Box>
                                }
                                </Box>
                            </Box >
                        )
            }
            {
                toDelete &&
                <ConfirmationDialogCaptcha
                    open={toDelete}
                    title="Confirmação"
                    description={
                        <Box>
                            Para remover a jornada {toDelete.name}, digite: <span style={{ 'fontWeight': 'bold' }}>remover</span> no campo abaixo.
                        </Box>
                    }
                    handleClose={() => setToDelete(null)}
                    handleSubmit={() => deleteJourney(toDelete)}
                    captcha={'remover'}
                />
            }
            {
                showCreateUpdateJourney && (
                    <Dialog open={showCreateUpdateJourney} onClose={() => setShowCreateUpdateJourney(false)} fullWidth maxWidth="sm">
                        <DialogTitle>{(newJourney.id) ? "Editar jornada" : "Nova jornada"}</DialogTitle>
                        <DialogContent>
                            <Box className="journeys-dialog-content">
                                <TextField label="Nome" size="small" fullWidth
                                    value={newJourney.name}
                                    onChange={(event) => setNewJourney((prevState) => ({ ...prevState, name: event.target.value }))}
                                    inputProps={{
                                        maxLength: 50
                                    }}
                                />
                                <TextField label="Descrição" fullWidth multiline rows={3} size="small"
                                    value={newJourney.description}
                                    onChange={(event) => setNewJourney((prevState) => ({ ...prevState, description: event.target.value }))}
                                />
                                <MultiSelectInput
                                    options={users}
                                    selected={newJourney.users}
                                    setSelected={handleSelectedUsers}
                                    fixedIds={supervisorFixedIds}
                                />
                                <FormControl fullWidth size="small">
                                    <InputLabel>Cor</InputLabel>
                                    <Select label="Cor"
                                        value={newJourney.color}
                                        onChange={(event) => setNewJourney((prevState) => ({ ...prevState, color: event.target.value }))}
                                    >
                                        {colorOptionsMedium.map((color) => (
                                            <MenuItem key={color.value} value={color.value}>
                                                <Box className="color-options" sx={{ backgroundColor: color.value }} />
                                                {color.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2">Campos obrigatórios da oportunidade</Typography>
                                    <FormGroup row>
                                        {[
                                            { key: 'title', label: 'Título' },
                                            { key: 'description', label: 'Descrição' },
                                            { key: 'value', label: 'Valor estimado' },
                                            { key: 'contact', label: 'Contato' },
                                            { key: 'contact_enterprise', label: 'Empresa do contato' },
                                            { key: 'owner', label: 'Responsável' },
                                            { key: 'estimated_at', label: 'Data estimada' },
                                            { key: 'status', label: 'Status' },
                                        ].map((f) => (
                                            <FormControlLabel
                                                key={f.key}
                                                control={<Switch
                                                    checked={Array.isArray(newJourney.required_opportunity_fields) && newJourney.required_opportunity_fields.includes(f.key)}
                                                    onChange={(e) => {
                                                        setNewJourney((prev) => {
                                                            const arr = Array.isArray(prev.required_opportunity_fields) ? [...prev.required_opportunity_fields] : []
                                                            if (e.target.checked) {
                                                                if (!arr.includes(f.key)) arr.push(f.key)
                                                            } else {
                                                                const idx = arr.indexOf(f.key)
                                                                if (idx > -1) arr.splice(idx, 1)
                                                            }
                                                            return { ...prev, required_opportunity_fields: arr }
                                                        })
                                                    }}
                                                />}
                                                label={f.label}
                                            />
                                        ))}
                                    </FormGroup>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowCreateUpdateJourney(false)}>Cancelar</Button>
                            <Button onClick={() => (newJourney.id) ? updateJourney(newJourney) : createJourney(newJourney)} disabled={!newJourney.name}>
                                {(newJourney.id) ? "Salvar" : "Criar"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )
            }
            {
                showEditRequiredFields && editingRequiredJourney && (
                    <Dialog open={showEditRequiredFields} onClose={() => { setShowEditRequiredFields(false); setEditingRequiredJourney(null); }} fullWidth maxWidth="sm">
                        <DialogTitle>Campos obrigatórios — {editingRequiredJourney.name}</DialogTitle>
                        <DialogContent>
                            <Box sx={{ mt: 1 }}>
                                <FormGroup>
                                    {[
                                        { key: 'title', label: 'Título' },
                                        { key: 'description', label: 'Descrição' },
                                        { key: 'value', label: 'Valor estimado' },
                                        { key: 'contact', label: 'Contato' },
                                        { key: 'contact_enterprise', label: 'Empresa do contato' },
                                        { key: 'owner', label: 'Responsável' },
                                        { key: 'estimated_at', label: 'Data estimada' },
                                        { key: 'status', label: 'Status' },
                                    ].map((f) => {
                                        const checked = Array.isArray(editingRequiredJourney.required_opportunity_fields) && editingRequiredJourney.required_opportunity_fields.includes(f.key)
                                        return (
                                            <FormControlLabel
                                                key={f.key}
                                                control={<Switch
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        setEditingRequiredJourney(prev => {
                                                            const arr = Array.isArray(prev.required_opportunity_fields) ? [...prev.required_opportunity_fields] : []
                                                            if (e.target.checked) {
                                                                if (!arr.includes(f.key)) arr.push(f.key)
                                                            } else {
                                                                const idx = arr.indexOf(f.key)
                                                                if (idx > -1) arr.splice(idx, 1)
                                                            }
                                                            return { ...prev, required_opportunity_fields: arr }
                                                        })
                                                    }}
                                                />}
                                                label={f.label}
                                            />
                                        )
                                    })}
                                </FormGroup>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setShowEditRequiredFields(false); setEditingRequiredJourney(null); }}>Cancelar</Button>
                            <Button onClick={async () => {
                                if (!editingRequiredJourney) return
                                const enterpriseId = getSession().profile.enterprise.id
                                const payload = { required_opportunity_fields: editingRequiredJourney.required_opportunity_fields || [] }
                                if (editingRequiredJourney.color) payload.color = editingRequiredJourney.color
                                const resp = await api.patch(`api/enterprises/${enterpriseId}/journeys/${editingRequiredJourney.id}/`, payload)
                                if (resp.status === 200) {
                                    setJourneys(prev => prev.map(j => j.id === resp.data.id ? resp.data : j))
                                    setShowEditRequiredFields(false)
                                    setEditingRequiredJourney(null)
                                    useNotification('Sucesso', 'Campos obrigatórios atualizados.', 'success')
                                } else {
                                    useNotification('Ops', 'Erro ao atualizar campos obrigatórios.', 'danger')
                                }
                            }} variant="contained">Salvar</Button>
                        </DialogActions>
                    </Dialog>
                )
            }
            {
                showDuplicateModal && (
                    <Dialog open={showDuplicateModal} onClose={() => setShowDuplicateModal(false)} fullWidth maxWidth="sm">
                        <DialogTitle>Copiar Jornada</DialogTitle>
                        <DialogContent>
                            <Box className="journeys-dialog-content">
                                <TextField label="Nome" size="small" fullWidth
                                    value={duplicateJourney?.name || ''}
                                    onChange={(event) => setDuplicateJourney((prevState) => ({ ...prevState, name: event.target.value }))}
                                    inputProps={{
                                        maxLength: 50
                                    }}
                                />
                                <TextField label="Descrição" fullWidth multiline rows={3} size="small"
                                    value={duplicateJourney?.description || ''}
                                    onChange={(event) => setDuplicateJourney((prevState) => ({ ...prevState, description: event.target.value }))}
                                />
                                <MultiSelectInput
                                    options={users}
                                    selected={duplicateJourney?.users || []}
                                    setSelected={(newUsers) => setDuplicateJourney((prevState) => ({ ...prevState, users: newUsers }))}
                                />
                                <FormControl fullWidth size="small">
                                    <InputLabel>Cor</InputLabel>
                                    <Select label="Cor"
                                        value={duplicateJourney?.color || '#6ca8e3'}
                                        onChange={(event) => setDuplicateJourney((prevState) => ({ ...prevState, color: event.target.value }))}
                                    >
                                        {colorOptionsMedium.map((color) => (
                                            <MenuItem key={color.value} value={color.value}>
                                                <Box className="color-options" sx={{ backgroundColor: color.value }} />
                                                {color.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={duplicateColumns}
                                                onChange={(e) => {
                                                    setDuplicateColumns(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setPreviousAutomationsState(duplicateAutomations);
                                                        setDuplicateAutomations(false);
                                                    } else {
                                                        setDuplicateAutomations(previousAutomationsState);
                                                    }
                                                }}
                                            />
                                        }
                                        label="Duplicar colunas"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={duplicateAutomations}
                                                onChange={(e) => setDuplicateAutomations(e.target.checked)}
                                                disabled={!duplicateColumns}
                                            />
                                        }
                                        label="Duplicar automações"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={duplicateRequiredFields}
                                                onChange={(e) => setDuplicateRequiredFields(e.target.checked)}
                                                disabled={!duplicateColumns}
                                            />
                                        }
                                        label="Duplicar campos obrigatórios"
                                    />
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowDuplicateModal(false)}>Cancelar</Button>
                            <Button onClick={() => {
                                const { id, ...dataToSend } = duplicateJourney;
                                dataToSend.duplicate_columns = duplicateColumns;
                                dataToSend.duplicate_automations = duplicateAutomations;
                                dataToSend.duplicate_required_fields = duplicateRequiredFields;
                                duplicateJourneyAction(id, dataToSend);
                            }} disabled={!duplicateJourney?.name}>
                                Salvar
                            </Button>
                        </DialogActions>
                    </Dialog>
                )
            }
        </>
    )
}

export default Journeys
